import React, { useCallback, useMemo, type MouseEvent } from 'react';
import isEmpty from 'lodash/isEmpty';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItem,
	DropdownItemCheckbox,
	DropdownItemGroup,
	DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import CheckboxCheckedIcon from '@atlaskit/icon/core/checkbox-checked';
import CheckboxUncheckedIcon from '@atlaskit/icon/core/checkbox-unchecked';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import Tooltip from '@atlaskit/tooltip';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	useChildIssuesLimit,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { HIDE_DONE_SUBTASKS_FILTER } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import type { ChildIssuesLimitStatus } from '@atlassian/jira-issue-view-common-constants/src/child-issues.tsx';
import {
	type ChildIssue,
	getChildIssuesLimitExceededAnalyticsCount,
} from '@atlassian/jira-issue-view-common-types/src/children-issues-type.tsx';

import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useProjectKey,
	useProjectType,
	useIsSimplifiedProject,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { ButtonWrapper } from '@atlassian/jira-issue-field-status/src/common/ui/view-workflow/button/index.tsx';
import type { MainIssueAggQueryRelayFragment } from '@atlassian/jira-issue-fetch-services-common/src/services/issue-agg-data/main.tsx';
import type { ChildIssuesPanelType } from '../../../../common/types.tsx';
import { generateViewInSearchLink } from '../../../../common/utils/generate-view-in-search-link/index.tsx';
import {
	hasExceededIssuesLimitAfterLoad,
	hasExceededIssuesLimitInitialLoad,
} from '../../../../common/utils/has-exceeded-issues-limit/index.tsx';
import { useChildIssuesSortBy } from '../../../../controllers/main.tsx';
import ActionsSortByMenu from '../../actions-sort-by-menu/index.tsx';
import CollectFeedbackActionItem from '../../collect-feedback-action-item/index.tsx';
import SortByRankButton from '../../sort-by-rank-button/index.tsx';
import WhatsNewSpotlightButton from '../../whats-new-spotlight-button/index.tsx';
import messages from './messages.tsx';
import { generateBulkEditLink, triggerButtonTestId } from './utils.tsx';
import { useUpdateHideDoneStateMutation } from './services/update-hide-done-state/index.tsx';

export type Props = {
	childIssuesPanelType: ChildIssuesPanelType;
	parentKey: IssueKey;
	hideDone?: boolean;
	allIssues: ChildIssue[];
	incompleteIssues: ChildIssue[];
	childIssuesLimitStatus: ChildIssuesLimitStatus;
	isSortingAvailable: boolean;
	onHideDoneClicked: (
		totalIssues: number,
		incompleteIssues: number,
		hideDone: boolean,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	onBulkEditClicked: (issueCount: number | string, analyticsEvent: UIAnalyticsEvent) => void;
	onViewInSearchClicked: (issueCount: number | string, analyticsEvent: UIAnalyticsEvent) => void;
	rootRelayFragment: MainIssueAggQueryRelayFragment | null;
	isHideDoneFilterEnabled: boolean | undefined | null;
	// TODO: Make required on hide-rank-option-when-unavailable cleanup
	isRankingEnabled?: boolean;
};

export const Meatballs = ({
	childIssuesLimitStatus,
	childIssuesPanelType,
	parentKey,
	hideDone = false,
	allIssues,
	incompleteIssues,
	isSortingAvailable,
	onHideDoneClicked,
	onBulkEditClicked,
	onViewInSearchClicked,
	rootRelayFragment,
	isHideDoneFilterEnabled,
	isRankingEnabled,
}: Props) => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const [sortByValue] = useChildIssuesSortBy();

	// TODO: When cleaning up fg('key-chin-hide-done-relay-migration') remove the redux usages for hideDonePreference
	const [hideDonePreference, { setValue: setHideDonePreference }] =
		useUserPreferencesValue(HIDE_DONE_SUBTASKS_FILTER);

	const issueKey = useIssueKey();

	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const isSimplifiedProject = useIsSimplifiedProject(projectKey);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const toggleIssueHideDone = useUpdateHideDoneStateMutation();

	const onBulkEditClickedAnalytics = useCallback(
		(issueCount: number | string) => (analyticsEvent: UIAnalyticsEvent) => {
			onBulkEditClicked(issueCount, analyticsEvent);
		},
		[onBulkEditClicked],
	);

	const onViewInSearchClickedAnalytics = useCallback(
		(issueCount: number | string) => (analyticsEvent: UIAnalyticsEvent) => {
			onViewInSearchClicked(issueCount, analyticsEvent);
		},
		[onViewInSearchClicked],
	);

	const onHideDoneClickedWithMyPreferences = useCallback(() => {
		// myPreferences can only be set as a string, not as a boolean
		const hasHideDonePreference = fg('key-chin-hide-done-relay-migration')
			? isHideDoneFilterEnabled === true
			: hideDonePreference === 'true';
		if (fg('key-chin-hide-done-relay-migration')) {
			toggleIssueHideDone({
				isHideDoneEnabled: !hasHideDonePreference,
				rootRelayFragment,
			});
		} else {
			setHideDonePreference(String(!hasHideDonePreference));
		}

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'item',
			attributes: {
				childIssuesPanelType,
				totalIssues: allIssues.length,
				incompleteIssues: incompleteIssues.length,
				hideDone: !hasHideDonePreference,
			},
		});

		onHideDoneClicked(
			allIssues.length,
			incompleteIssues.length,
			!hasHideDonePreference,
			analyticsEvent,
		);
	}, [
		isHideDoneFilterEnabled,
		hideDonePreference,
		createAnalyticsEvent,
		childIssuesPanelType,
		allIssues.length,
		incompleteIssues.length,
		onHideDoneClicked,
		toggleIssueHideDone,
		rootRelayFragment,
		setHideDonePreference,
	]);

	const isWithinLimitOnInitialLoad = useMemo(
		() => !hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus),
		[childIssuesLimitStatus],
	);

	const isHideDoneCheckboxVisible = useMemo(
		() =>
			expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false)
				? isWithinLimitOnInitialLoad
				: !isEmpty(incompleteIssues) &&
					incompleteIssues.length !== allIssues.length &&
					isWithinLimitOnInitialLoad,
		[allIssues.length, isWithinLimitOnInitialLoad, incompleteIssues],
	);

	const viewInSearchLink = useMemo(() => {
		// These values will never be missing when the FF is enabled, this check is to satisfy TS
		if (projectType === undefined || isSimplifiedProject === undefined) {
			logger.safeErrorWithoutCustomerData(
				'issue.child-issues-panel',
				'Missing project, type, or isSimplified when generating search link',
			);
			return '';
		}

		return generateViewInSearchLink(
			projectKey,
			projectType,
			isSimplifiedProject,
			parentKey,
			childIssuesPanelType,
			hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus),
			sortByValue,
		);
	}, [
		childIssuesLimitStatus,
		childIssuesPanelType,
		isSimplifiedProject,
		parentKey,
		projectKey,
		projectType,
		sortByValue,
	]);

	const onViewInSearchClickedCallback = useCallback(
		(visibleIssuesCount: number | string) =>
			(event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
				onViewInSearchClickedAnalytics(visibleIssuesCount)(analyticsEvent);
				// Ignore cmd or ctrl clicks - allow user to open in new tab
				if (!event.metaKey && !event.ctrlKey) {
					event.preventDefault();
					push(viewInSearchLink);
				}
			},
		[push, onViewInSearchClickedAnalytics, viewInSearchLink],
	);

	// TODO: To be removed on clean-up of ('jira-issue-view-chin-ga')
	const renderFilters = useCallback(() => {
		const hasExceededLimitAfterLoad = hasExceededIssuesLimitAfterLoad(childIssuesLimitStatus);
		const renderFiltersDropdownGroup = isHideDoneCheckboxVisible;
		const hideDoneToolTip = hasExceededLimitAfterLoad
			? formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.disabledHideDoneIssueTermRefresh
						: messages.disabledHideDone,
				)
			: formatMessage(messages.hideDoneTooltip);

		return renderFiltersDropdownGroup ? (
			<DropdownItemGroup
				title={formatMessage(messages.filter)}
				id="child-issues-panel-filter-group"
			>
				{isHideDoneCheckboxVisible && (
					<DropdownItemCheckboxGroup id="child-issues-panel-checkbox-group">
						<Tooltip content={hideDoneToolTip} position="top">
							<DropdownItemCheckbox
								id="hideDone"
								key="hideDone"
								onClick={onHideDoneClickedWithMyPreferences}
								isSelected={
									fg('key-chin-hide-done-relay-migration')
										? isHideDoneFilterEnabled === true
										: hideDonePreference === 'true'
								}
								isDisabled={hasExceededLimitAfterLoad}
							>
								{formatMessage(messages.hideDone)}
							</DropdownItemCheckbox>
						</Tooltip>
					</DropdownItemCheckboxGroup>
				)}
			</DropdownItemGroup>
		) : null;
	}, [
		childIssuesLimitStatus,
		formatMessage,
		hideDonePreference,
		isHideDoneFilterEnabled,
		isHideDoneCheckboxVisible,
		onHideDoneClickedWithMyPreferences,
	]);

	const renderHideDoneFilter = useCallback(() => {
		const hasExceededLimitAfterLoad = hasExceededIssuesLimitAfterLoad(childIssuesLimitStatus);
		const renderFiltersDropdownGroup = isHideDoneCheckboxVisible;
		const hideDoneToolTip = hasExceededLimitAfterLoad
			? formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.disabledHideDoneIssueTermRefresh
						: messages.disabledHideDone,
				)
			: formatMessage(messages.hideDoneTooltip);

		return renderFiltersDropdownGroup && isHideDoneCheckboxVisible ? (
			<Tooltip content={hideDoneToolTip} position="top">
				<DropdownItem
					isSelected={
						fg('key-chin-hide-done-relay-migration')
							? isHideDoneFilterEnabled === true
							: hideDonePreference === 'true'
					}
					onClick={onHideDoneClickedWithMyPreferences}
					elemBefore={
						(
							fg('key-chin-hide-done-relay-migration')
								? isHideDoneFilterEnabled === true
								: hideDonePreference === 'true'
						) ? (
							<CheckboxCheckedIcon color="currentColor" label="" />
						) : (
							<CheckboxUncheckedIcon color="currentColor" label="" />
						)
					}
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					testId="issue.views.common.child-issues-panel.heading.meatballs.hide-done-dropdown-item"
					isDisabled={hasExceededLimitAfterLoad}
				>
					{formatMessage(messages.hideDone)}
				</DropdownItem>
			</Tooltip>
		) : null;
	}, [
		childIssuesLimitStatus,
		formatMessage,
		hideDonePreference,
		isHideDoneFilterEnabled,
		isHideDoneCheckboxVisible,
		onHideDoneClickedWithMyPreferences,
	]);

	const childIssuesLimit = useChildIssuesLimit();

	const childIssuesLimitExceededAnalyticsCount =
		getChildIssuesLimitExceededAnalyticsCount(childIssuesLimit);

	const renderedActions = useMemo(() => {
		const visibleIssues = hideDone ? incompleteIssues : allIssues;
		const visibleIssuesCount = hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus)
			? childIssuesLimitExceededAnalyticsCount
			: visibleIssues.length;
		const bulkEditLink = generateBulkEditLink(
			parentKey,
			childIssuesPanelType,
			hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus),
			sortByValue || undefined,
		);

		return (
			<>
				<DropdownItem
					// @ts-expect-error Type '(analyticsEvent: AnalyticsEvent) => void' is not assignable to type '(e: MouseEvent<Element, MouseEvent> | KeyboardEvent<Element>) => void'.
					onClick={onBulkEditClickedAnalytics(visibleIssuesCount)}
					href={bulkEditLink}
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					data-testid="issue.views.common.child-issues-panel.heading.meatballs.bulk-edit-dropdown-item"
					UNSAFE_shouldDisableRouterLink
				>
					{formatMessage(messages.bulkEdit)}
				</DropdownItem>
				<DropdownItem
					// @ts-expect-error - TS2769 - No overload matches this call.
					onClick={onViewInSearchClickedCallback(visibleIssuesCount)}
					href={viewInSearchLink}
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					data-testid="issue.views.common.child-issues-panel.heading.meatballs.view-in-search-dropdown-item"
					UNSAFE_shouldDisableRouterLink
				>
					{formatMessage(messages.search)}
				</DropdownItem>
			</>
		);
	}, [
		hideDone,
		incompleteIssues,
		allIssues,
		childIssuesLimitStatus,
		childIssuesLimitExceededAnalyticsCount,
		parentKey,
		childIssuesPanelType,
		sortByValue,
		formatMessage,
		onBulkEditClickedAnalytics,
		onViewInSearchClickedCallback,
		viewInSearchLink,
	]);

	const renderDropdownTriggerButton = useCallback(
		({ triggerRef, ...triggerProps }: CustomTriggerProps<HTMLButtonElement>) => (
			<IconButton
				{...triggerProps}
				appearance="subtle"
				spacing="compact"
				icon={(iconProps) => (
					<ShowMoreHorizontalIcon color="currentColor" {...iconProps} LEGACY_size="small" />
				)}
				label={formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.tooltipIssueTermRefresh
						: messages.tooltip,
				)}
				ref={triggerRef}
				interactionName="auto-generated.jira-issue-view-common-views.child-issues-panel.view.heading.meatballs.button"
			/>
		),
		[formatMessage],
	);

	const actionsTitle = isHideDoneCheckboxVisible ? formatMessage(messages.actions) : undefined;

	if (
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('chin-refactor-and-prune-jiv-child-issues-sorting') &&
		fg('hide-rank-option-when-unavailable') &&
		expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false)
	) {
		return (
			<ButtonWrapper data-testid={triggerButtonTestId}>
				<DropdownMenu placement="bottom-end" trigger={renderDropdownTriggerButton}>
					{renderHideDoneFilter()}
					{isSortingAvailable && isRankingEnabled ? (
						<SortByRankButton childIssuesLimitStatus={childIssuesLimitStatus} />
					) : null}
					<DropdownItemGroup hasSeparator>{renderedActions}</DropdownItemGroup>
					<DropdownItemGroup hasSeparator>
						{fg('jiv-chin-spotlight-tour') ? <WhatsNewSpotlightButton /> : null}
						<CollectFeedbackActionItem />
					</DropdownItemGroup>
				</DropdownMenu>
			</ButtonWrapper>
		);
	}

	return expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false) ? (
		<ButtonWrapper data-testid={triggerButtonTestId}>
			<DropdownMenu placement="bottom-end" trigger={renderDropdownTriggerButton}>
				{renderHideDoneFilter()}
				{isSortingAvailable ? (
					<ActionsSortByMenu
						childIssuesLimitStatus={childIssuesLimitStatus}
						isRankingEnabled={
							fg('hide-rank-option-when-unavailable') ? isRankingEnabled : undefined
						}
					/>
				) : null}
				<DropdownItemGroup hasSeparator>
					{renderedActions}
					{fg('jiv-chin-spotlight-tour') ? null : <CollectFeedbackActionItem />}
				</DropdownItemGroup>
				{fg('jiv-chin-spotlight-tour') ? (
					<DropdownItemGroup hasSeparator>
						<WhatsNewSpotlightButton />
						<CollectFeedbackActionItem />
					</DropdownItemGroup>
				) : null}
			</DropdownMenu>
		</ButtonWrapper>
	) : (
		<ButtonWrapper data-testid={triggerButtonTestId}>
			<DropdownMenu placement="bottom-end" trigger={renderDropdownTriggerButton}>
				{renderFilters()}
				<DropdownItemGroup
					testId="issue-view-common-views.child-issues-panel.heading.meatballs.dropdown-item-group"
					title={actionsTitle}
				>
					{renderedActions}
				</DropdownItemGroup>
			</DropdownMenu>
		</ButtonWrapper>
	);
};

export default Meatballs;
